// frontend/src/components/ParameterSearchInput.js
// Search input component for Patient Flowsheet parameters

import React from 'react';
import { Input, List, Typography, Tag, Spin } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useParameterSearch } from '../hooks/useParameterSearch';

const { Text } = Typography;

/**
 * Parameter search input component with autocomplete dropdown
 * @param {Function} onParameterSelect - Callback when parameter is selected
 * @param {string} placeholder - Input placeholder text
 * @param {Object} style - Additional styles for the container
 */
const ParameterSearchInput = ({
  onParameterSelect,
  onSearchClear,
  placeholder,
  style = {}
}) => {
  const { t } = useTranslation();
  const {
    searchQuery,
    searchResults,
    isSearching,
    showResults,
    handleSearchChange,
    handleParameterSelect,
    handleKeyDown,
    handleSearchFocus,
    handleSearchBlur,
    clearSearch,
    getHighlightedResult,
    hasResults,
  } = useParameterSearch(onParameterSelect, 25);

  const containerStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: '600px',
    ...style,
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #d9d9d9',
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
  };

  const listItemStyle = (isHighlighted) => ({
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: isHighlighted ? '#f6ffed' : '#fff',
    transition: 'background-color 0.2s',
  });

  const renderSearchResult = (item, index) => {
    const isHighlighted = getHighlightedResult(index);
    
    return (
      <div
        key={item.key}
        style={listItemStyle(isHighlighted)}
        onClick={() => handleParameterSelect(item)}
        onMouseEnter={() => {
          // Optional: could set selectedIndex on hover
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: '14px', color: '#262626' }}>
              {item.label}
            </Text>
            <div style={{ marginTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {item.description}
              </Text>
              {item.units && (
                <Tag size="small" style={{ marginLeft: '8px', fontSize: '11px' }}>
                  {item.units}
                </Tag>
              )}
            </div>
            <div style={{ marginTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                Key: {item.key}
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <Input
        placeholder={placeholder || t('search.searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => handleSearchChange(e.target.value)}
        onKeyDown={handleKeyDown}

        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        suffix={
          searchQuery ? (
            <CloseOutlined
              style={{ color: '#bfbfbf', cursor: 'pointer' }}
              onClick={() => {
                clearSearch();
                if (onSearchClear) {
                  onSearchClear();
                }
              }}
            />
          ) : null
        }
        style={{
          borderRadius: showResults ? '6px 6px 0 0' : '6px',
          fontSize: '16px',
          padding: '12px 16px',
          height: '48px',
          border: '2px solid #d9d9d9',
          transition: 'all 0.3s ease',
        }}
        onFocus={(e) => {
          handleSearchFocus();
          e.target.style.borderColor = '#1890ff';
          e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
        }}
        onBlur={(e) => {
          handleSearchBlur();
          e.target.style.borderColor = '#d9d9d9';
          e.target.style.boxShadow = 'none';
        }}
        allowClear={false} // We handle clear manually
      />
      
      {showResults && (
        <div style={dropdownStyle}>
          {isSearching ? (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Spin size="small" />
              <Text type="secondary" style={{ marginLeft: '8px' }}>
                {t('search.searching')}
              </Text>
            </div>
          ) : hasResults ? (
            <div>
              {searchResults.map((item, index) => renderSearchResult(item, index))}
            </div>
          ) : (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Text type="secondary">
                {t('search.noResults')} "{searchQuery}"
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParameterSearchInput;
